import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Mic } from "lucide-react";
import { GOLD, CRIMSON, StatusLight } from "./AnalysisStation";

const BARS = 56;
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Deterministic pseudo-waveform so the brief looks like a real recording without decoding audio.
const heights = (seed) => {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return Array.from({ length: BARS }, (_, i) => {
    h = (h * 1103515245 + 12345) >>> 0;
    const env = Math.sin((i / BARS) * Math.PI) * 0.6 + 0.4;
    return 0.18 + ((h >>> 16) % 1000) / 1000 * 0.82 * env;
  });
};

// Coach's recorded voice note, surfaced as a tap-to-play brief. Only rendered when a recording exists.
export const CoachVoiceBrief = ({ proof, api, token }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const bars = React.useMemo(() => heights(proof.id), [proof.id]);
  useEffect(() => { setPlaying(false); setCur(0); setDur(0); }, [proof.id]);
  if (!proof.voice_path) return null;
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) a.play().catch(() => {}); else a.pause(); };
  const progress = dur ? cur / dur : 0;
  return (
    <div data-testid={`voice-brief-${proof.id}`} className="rounded-xl border border-[#F5C518]/30 p-3 sm:p-4"
      style={{ background: "linear-gradient(180deg, rgba(22,22,22,0.92), rgba(8,8,8,0.96))", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 18px 50px rgba(0,0,0,0.55)" }}>
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] uppercase text-[#F5C518]"><Mic size={12} /> Coach_Voice_Brief</span>
        <span className="flex items-center gap-1.5 font-mono text-[8px] tracking-[0.25em] uppercase text-white/45">
          {playing ? <StatusLight color={CRIMSON} size={6} /> : <span className="inline-block w-1.5 h-1.5 rounded-full bg-white/25" />}{playing ? "Playing" : "Tap to play"}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" data-testid={`voice-brief-play-${proof.id}`} onClick={toggle} aria-label={playing ? "Pause coach voice brief" : "Play coach voice brief"}
          className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-black transition-transform hover:scale-105 active:scale-95"
          style={{ background: GOLD, boxShadow: `0 0 18px ${GOLD}88` }}>
          {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div data-testid={`voice-brief-wave-${proof.id}`} className="flex items-center gap-[2px] h-12 cursor-pointer" onClick={(ev) => {
            const a = ref.current; if (!a || !dur) return;
            const r = ev.currentTarget.getBoundingClientRect(); a.currentTime = ((ev.clientX - r.left) / r.width) * dur;
          }}>
            {bars.map((h, i) => {
              const done = i / BARS <= progress;
              const near = playing && Math.abs(i / BARS - progress) < 0.04;
              return (
                <motion.span key={i} className="flex-1 rounded-full" style={{ background: done ? GOLD : "rgba(255,255,255,0.18)", boxShadow: done ? `0 0 6px ${GOLD}66` : "none" }}
                  animate={{ height: `${h * 100 * (near ? 1.25 : 1)}%` }} transition={{ duration: 0.18 }} />
              );
            })}
          </div>
          <div className="flex justify-between font-mono text-[8px] tracking-[0.2em] text-white/40 mt-1">
            <span data-testid={`voice-brief-time-${proof.id}`}>{fmt(cur)}</span><span>{dur ? fmt(dur) : "--:--"}</span>
          </div>
        </div>
      </div>
      <audio ref={ref} preload="metadata" src={`${api}/proofs/voice/${proof.id}?auth=${token}`}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setCur(0); }}
        onTimeUpdate={(ev) => setCur(ev.target.currentTime || 0)} onLoadedMetadata={(ev) => setDur(isFinite(ev.target.duration) ? ev.target.duration : 0)} onDurationChange={(ev) => setDur(isFinite(ev.target.duration) ? ev.target.duration : 0)} />
    </div>
  );
};
